import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';
import axiosInstance from 'src/api/axiosInstance';
import { useSettingsContext } from 'src/components/settings';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import Iconify from 'src/components/iconify';
import { fetchEncryptedToken } from 'src/utils/encryptUtils';

const AVATAR_COLORS = [
  '#6366f1', // Indigo
  '#ec4899', // Pink
  '#10b981', // Emerald
  '#f59e0b', // Amber
  '#0ea5e9', // Sky
  '#8b5cf6', // Violet
  '#14b8a6', // Teal
  '#f97316', // Orange
];

const stringToColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export default function CandidateAvatarTooltip({ candidateId, candidateName, rowData, onView, readOnly = false }) {
  const router = useRouter();
  const settings = useSettingsContext();
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const [imgSrc, setImgSrc] = useState(null);

  const nm = candidateName || '-';
  const profPic =
    rowData?.profilePhotoUrl ||
    rowData?.profilePhoto ||
    rowData?.avatarUrl ||
    rowData?.profilePic ||
    rowData?.photo ||
    rowData?.profileP;
  const candStatus = rowData?.candStatus || '-';
  const email = rowData?.email || '-';
  const mob = rowData?.mob || '-';
  const priSkl = rowData?.priSkl || '';
  const positionName = rowData?.positionName || '';
  let chipColor = 'info';
  if (candStatus === 'Joined') {
    chipColor = 'success';
  } else if (candStatus === 'Rejected') {
    chipColor = 'error';
  }
  const bgColor = stringToColor(nm);
  const textColor = '#fff';
  const avatarColor = settings.themeMode === 'dark' ? '#333b48' : '#e4e7eb';

  // Extracts the S3 object key from either a raw key or a full pre-signed URL
  const extractS3Key = (val) => {
    if (!val) return null;
    if (!val.startsWith('http://') && !val.startsWith('https://')) return val; // already a key
    try {
      const url = new URL(val);
      return decodeURIComponent(url.pathname.replace(/^\/+/, '')); // strip leading '/'
    } catch {
      return null;
    }
  };

  useEffect(() => {
    let isMounted = true;
    const resolvePhotoUrl = async () => {
      if (!profPic || profPic === '-' || profPic === 'null' || profPic === 'undefined') {
        if (isMounted) setImgSrc(null);
        return;
      }

      if (
        typeof profPic === 'string' &&
        (profPic.startsWith('data:') ||
          profPic.startsWith('http://') ||
          profPic.startsWith('https://'))
      ) {
        if (isMounted) setImgSrc(profPic);
        return;
      }

      if (
        typeof profPic === 'string' &&
        (profPic.startsWith('/assets/') || profPic.startsWith('/api/'))
      ) {
        const base = (axiosInstance.defaults.baseURL || '').replace(/\/$/, '').replace(/\/api.*$/, '');
        if (isMounted) setImgSrc(`${base}${profPic}`);
        return;
      }

      const key = extractS3Key(profPic);
      if (!key) {
        if (isMounted) setImgSrc(null);
        return;
      }

      try {
        const res = await axiosInstance.get('/rest/candidate/profile-photo', {
          params: { objectKey: key },
          headers: { Authorization: `Bearer ${token}` },
        });
        const photoUrl = res.data?.photoUrl || (typeof res.data === 'string' ? res.data : null);
        if (isMounted && photoUrl) {
          if (typeof photoUrl === 'string' && (photoUrl.startsWith('/api/') || photoUrl.startsWith('/assets/'))) {
            const base = (axiosInstance.defaults.baseURL || '').replace(/\/$/, '').replace(/\/api.*$/, '');
            setImgSrc(`${base}${photoUrl}`);
          } else {
            setImgSrc(photoUrl);
          }
        } else if (isMounted) {
          setImgSrc(null);
        }
      } catch (err) {
        console.error('Failed to load profile photo for candidate tooltip:', err);
        if (isMounted) setImgSrc(null);
      }
    };

    resolvePhotoUrl();
    return () => {
      isMounted = false;
    };
  }, [profPic, token]);

  const handleViewRow = async () => {
    if (onView) {
      onView();
      return;
    }
    if (!candidateId) return;
    try {
      const encryptedId = await fetchEncryptedToken(candidateId, token);
      if (encryptedId) {
        router.push(paths.dashboard.recruitment.candidate.edit(encryptedId));
      }
    } catch (err) {
      console.error('Failed to navigate to candidate details:', err);
    }
  };

  return (
    <Tooltip
      title={
        <Box position="relative" display="flex" flexDirection="column" gap={2}>
          {/* Maximize Button */}
          {candidateId && !readOnly && (
            <IconButton
              size="small"
              onClick={handleViewRow}
              sx={{
                position: 'absolute',
                top: -4,
                right: -4,
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Iconify icon="solar:maximize-bold" width={18} />
            </IconButton>
          )}

          {/* Header profile details */}
          <Box display="flex" alignItems="center" gap={2} sx={{ pr: 3 }}>
            <Avatar
              alt={nm}
              src={imgSrc || undefined}
              sx={{
                width: 56,
                height: 56,
                fontSize: '1.4rem',
                fontWeight: 700,
                bgcolor: bgColor,
                color: textColor,
                border: `2px solid ${avatarColor}40`,
              }}
            >
              {nm.charAt(0).toUpperCase()}
            </Avatar>
            <Box display="flex" flexDirection="column">
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                {nm}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.5 }}>
                CANDIDATE ID: {candidateId}
              </Typography>
              {candStatus && candStatus !== '-' && (
                <Chip
                  label={candStatus.toUpperCase()}
                  size="small"
                  variant="soft"
                  color={chipColor}
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    borderRadius: 0.5,
                    alignSelf: 'flex-start',
                  }}
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ borderStyle: 'dashed' }} />

          {/* Contact Details List */}
          <Box display="flex" flexDirection="column" gap={1.5}>
            {/* Email Address */}
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: 'success.lighter',
                  color: 'success.main',
                }}
              >
                <Iconify icon="solar:letter-bold" width={18} />
              </Box>
              <Box display="flex" flexDirection="column">
                <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                  Email Address
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, wordBreak: 'break-all' }}>
                  {email}
                </Typography>
              </Box>
            </Box>

            {/* Contact Number */}
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: 'primary.lighter',
                  color: 'primary.main',
                }}
              >
                <Iconify icon="solar:phone-bold" width={18} />
              </Box>
              <Box display="flex" flexDirection="column">
                <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                  Contact Number
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                  {mob}
                </Typography>
              </Box>
            </Box>

            {/* Applied Position */}
            {positionName && (
              <Box display="flex" alignItems="center" gap={1.5}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: 'warning.lighter',
                    color: 'warning.main',
                  }}
                >
                  <Iconify icon="solar:case-bold" width={18} />
                </Box>
                <Box display="flex" flexDirection="column">
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                    Position Applied
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                    {positionName}
                  </Typography>
                </Box>
              </Box>
            )}


          </Box>
        </Box>
      }
      placement="right"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: settings.themeMode === 'dark' ? '#1e293b' : '#fff',
            color: 'text.primary',
            boxShadow: (theme) => theme.customShadows.z20,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            p: 2.5,
            borderRadius: 2,
            maxWidth: 320,
          },
        },
        arrow: {
          sx: {
            color: settings.themeMode === 'dark' ? '#1e293b' : '#fff',
            '&::before': {
              border: (theme) => `1px solid ${theme.palette.divider}`,
            },
          },
        },
      }}
    >
      <Avatar
        alt={nm}
        src={imgSrc || undefined}
        sx={{
          width: 36,
          height: 36,
          fontSize: '0.9rem',
          fontWeight: 700,
          bgcolor: `${bgColor}1A`,
          color: bgColor,
          cursor: 'pointer',
          border: `1px solid ${bgColor}20`,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'scale(1.08)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        {nm.charAt(0).toUpperCase()}
      </Avatar>
    </Tooltip>
  );
}

CandidateAvatarTooltip.propTypes = {
  candidateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  candidateName: PropTypes.string,
  rowData: PropTypes.object,
  onView: PropTypes.func,
  readOnly: PropTypes.bool,
};
